import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import AuthService from "@/services/auth";
import { useGlobalStore } from "@/store/store";
import Logo from "@/assets/home/logo.svg?react";
import { useTranslation } from "@/locales";

/**
 * HKAI 第三方 JWT 登录页面
 * 跳转路径: /open/hkai?token=jwtKey
 * 流程: 解析 URL 中的 token → 调用 HKAI 登录接口 → 更新用户信息 → 跳转到首页
 */
const HkaiLoginPage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setUser = useGlobalStore((state) => state.setUser);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const token = searchParams.get("token");

  const doLogin = async (jwt: string) => {
    setError(null);
    setLoading(true);
    try {
      const userInfo = await AuthService.hkaiLogin(jwt);
      setUser(userInfo);
      navigate("/", { replace: true });
    } catch (err: any) {
      console.error("HKAI 登录失败:", err);
      setError(err?.message || t("open.loginFailed"));
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      setError(t("open.invalidVoucher"));
      setLoading(false);
      return;
    }
    doLogin(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-b from-white via-slate-50 to-slate-100 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800 px-6">
      {/* Logo 区域 */}
      <div className="flex flex-col items-center gap-4 mb-10 sm:mb-14">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-white dark:bg-slate-800 shadow-md flex items-center justify-center">
          <Logo className="w-12 h-12 sm:w-14 sm:h-14" />
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-white tracking-tight">
          BeeFintech AI
        </h1>
      </div>

      {/* 状态区域 */}
      {error ? (
        <div className="flex flex-col items-center gap-5 w-full max-w-xs">
          <div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/20 flex items-center justify-center">
            <svg
              className="w-8 h-8 text-red-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
              />
            </svg>
          </div>
          <p className="text-base text-slate-600 dark:text-slate-300 text-center">
            {error}
          </p>
          {token && (
            <button
              className="mt-2 w-full py-3 bg-slate-800 dark:bg-slate-600 text-white text-sm font-medium rounded-xl hover:bg-slate-700 dark:hover:bg-slate-500 active:scale-[0.98] transition-all"
              onClick={() => doLogin(token)}
            >
              {t("error.boundary.reLogin")}
            </button>
          )}
        </div>
      ) : loading ? (
        <div className="flex flex-col items-center gap-5">
          {/* 脉冲动画 */}
          <div className="relative flex items-center justify-center">
            <div className="absolute w-16 h-16 rounded-full bg-blue-500/10 animate-ping" />
            <div className="relative w-12 h-12 rounded-full border-[3px] border-slate-200 dark:border-slate-600 border-t-blue-500 animate-spin" />
          </div>
          <p className="text-base text-slate-400 dark:text-slate-500">
            {t("open.verifyLogin")}
          </p>
        </div>
      ) : null}
    </div>
  );
};

export default HkaiLoginPage;
